import React, { useEffect, useState } from "react";
import { trackEvent } from "@/lib/tracking";

// A quiet ribbon for paid visitors only. The Etsy promo code is the one
// instrument that ties a Meta-driven Etsy purchase back to the ad: Etsy hides
// referrers and has no orders API for us, but it counts redemptions by code.
// Shown when the visit carries ?promo=CODE, or arrived from a paid source
// (utm_source=meta) inside the offer window. Never shown to organic visitors,
// so the storefront price stays the storefront price.
const PROMO_KEY = "dw_promo";
// `ends` is Etsy's own cutoff for the code (Etsy shows "– 29 Sep 2026 00:00" shop time = 28 Sep 16:00Z), so the
// ribbon never promises a code Etsy has already stopped honouring. Etsy codes cannot be extended once created, so
// MEET15 hands over to WELCOME15 (same 15%, to 1 Oct) — a visitor arriving on a MEET15 link after the cutoff sees
// and gets WELCOME15. HAIKU15 (the direct-to-Etsy ad) is applied by Etsy itself and never passes through here.
const OFFERS = {
  MEET15:    { pct: 15, ends: "2026-09-28T16:00:00Z", label: "until 28 September", successor: "WELCOME15" },
  WELCOME15: { pct: 15, ends: "2026-10-01T15:59:59Z", label: "until 1 October" },
};

// Etsy shop-wide sale AUTUMN15 (founder 2026-09-23): 15% off every listing, 23 Sep – 5 Oct shop time. While it runs the
// discount is already in Etsy's price (strikethrough on every listing), codes do not stack on top of it (verified in a
// guest basket), so the ribbon says so plainly, shows to every shop visitor, and Buy links go straight to the listing.
const SHOP_SALE = { pct: 15, starts: "2026-09-22T16:00:00Z", ends: "2026-10-05T15:59:59Z", label: "until 5 October", code: "AUTUMN15" };

export function activeSale() {
  const now = new Date();
  return now >= new Date(SHOP_SALE.starts) && now <= new Date(SHOP_SALE.ends) ? SHOP_SALE : null;
}

export function activePromo() {
  try { return readPromo(); } catch { return null; }
}

// Etsy honours ?coupon= only on the SHOP page (banner + discount carried into the basket). On a LISTING page it is
// ignored for guests — verified 2026-09-19: same code, listing url → full price and no banner; shop url → "Success!
// … will be applied at checkout". So a visitor holding the code lands on the shop page with THEIR print searched up
// (each query returns exactly that print's editions); visitors without a code keep the direct listing link.
const ETSY_SHOP = "https://www.etsy.com/shop/DharmaWeaveArt";
const ETSY_SEARCH = {
  "basho-old-pond": "old pond",
  "basho-stillness-cicada-rock": "cicada",
  "issa-snail-mt-fuji": "snail",
  "basho-summer-grasses": "summer grasses",
  "shiki-persimmon-horyuji": "persimmon",
  "basho-crow-bare-branch": "crow",
  "dogen-boatman-cherry-petals": "dogen",
  "krishna-beautiful-portrait": "krishna",
};

export function withCoupon(url, productId) {
  if (activeSale()) return url;             // sale price is on the listing itself; land on the print
  const p = activePromo();
  if (!p || !/etsy\.com/.test(url)) return url;
  try {
    const src = new URL(url);
    const u = new URL(ETSY_SHOP);
    const q = ETSY_SEARCH[productId];
    if (q) u.searchParams.set("search_query", q);
    u.searchParams.set("coupon", p.code);
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content"]) {
      const v = src.searchParams.get(k);
      if (v) u.searchParams.set(k, v);
    }
    return u.toString();
  } catch { return url; }
}

function readPromo() {
  try {
    const url = new URL(window.location.href);
    const fromUrl = (url.searchParams.get("promo") || "").toUpperCase();
    const utmSource = url.searchParams.get("utm_source");
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(PROMO_KEY) || "null"); } catch {}
    let code = fromUrl || (utmSource === "meta" ? "MEET15" : null) || (stored && stored.code);
    if (!code || !OFFERS[code]) return null;
    if (new Date(OFFERS[code].ends) < new Date()) {
      const next = OFFERS[code].successor;
      if (!next || !OFFERS[next] || new Date(OFFERS[next].ends) < new Date()) return null;
      code = next;
    }
    if (!stored || stored.code !== code) {
      try { localStorage.setItem(PROMO_KEY, JSON.stringify({ code, seen: Date.now() })); } catch {}
    }
    return { code, ...OFFERS[code] };
  } catch {
    return null;
  }
}

export default function PromoRibbon({ where = "shop" }) {
  const [promo, setPromo] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const sale = activeSale();
    const p = sale ? { ...sale, sale: true } : readPromo();
    if (p) {
      setPromo(p);
      trackEvent("promo_view", { promo_code: p.code, where, kind: p.sale ? "shop_sale" : "code" });
    }
  }, [where]);
  if (!promo) return null;
  if (promo.sale) {
    return (
      <div className="max-w-5xl mx-auto px-5 md:px-10 mt-6">
        <div className="card-shrine rounded-sm px-5 py-4 flex flex-wrap items-center gap-x-4 gap-y-2 bg-cream/95">
          <p className="font-serif text-ink text-base md:text-lg leading-snug">
            {promo.pct}% off every print on Etsy
            <span className="text-ink-mute"> · {promo.label}</span>
          </p>
          <p className="font-body text-[0.7rem] text-ink-mute tracking-widest2 uppercase w-full">
            Already taken off the price, no code needed. Free US shipping stays.
          </p>
        </div>
      </div>
    );
  }
  const copy = async () => {
    try { await navigator.clipboard.writeText(promo.code); setCopied(true); } catch {}
    trackEvent("promo_copy", { promo_code: promo.code, where });
  };
  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 mt-6">
      <div className="card-shrine rounded-sm px-5 py-4 flex flex-wrap items-center gap-x-4 gap-y-2 bg-cream/95">
        <p className="font-serif text-ink text-base md:text-lg leading-snug">
          {promo.pct}% off any print at Etsy checkout with code{" "}
          <span className="font-display tracking-widest2">{promo.code}</span>
          <span className="text-ink-mute"> · {promo.label}</span>
        </p>
        <button onClick={copy} className="btn-pill text-sm" type="button">
          {copied ? "Copied" : "Copy code"}
        </button>
        <p className="font-body text-[0.7rem] text-ink-mute tracking-widest2 uppercase w-full">
          Enter it in the coupon box on Etsy before you pay. Free US shipping stays.{" "}
          <a
            href={`https://dharmaweaveart.etsy.com?coupon=${promo.code}`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-ink"
            onClick={() => trackEvent("promo_shop_link", { promo_code: promo.code, where })}
          >
            Or open the shop with it applied
          </a>
        </p>
      </div>
    </div>
  );
}
